import React, { useState, useEffect } from 'react';
import { useOrgAuth } from '../../../contexts/OrgAuthContext';
import {
  getCareConnectGroups, createCareConnectGroup,
  getSupportNetworkGroups, createSupportNetworkGroup, updateSupportNetworkGroup,
  getMentoringPairs, createMentoringPair, endMentoringPair,
  getPeerConnections, createPeerConnection, endPeerConnection,
  getPeerMessages,
  getPendingSupportRequests, getPendingVolunteerRoles, approveVolunteerRole,
  getConcerns, resolveConcern,
  getGroupJoinRequests, approveGroupJoinRequest, declineGroupJoinRequest,
} from '../../../api/programmesApi';
import {
  getVolunteers, getMembers,
} from '../../../api/communityApi';

const TABS = [
  { key: 'careConnect', label: 'Care & Connect' },
  { key: 'rebuild', label: 'Rebuild & Shine' },
  { key: 'widowToWidow', label: 'Widow-to-Widow' },
  { key: 'concerns', label: 'Concerns Log' },
];

const fmtDate = (ts) => ts ? new Date(ts?.toDate?.() || ts).toLocaleDateString('en-SG') : '—';
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};
const safeStr = (val) => (typeof val === 'string' ? val : '');

const addSixMonths = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const end = new Date(d);
  end.setMonth(end.getMonth() + 6);
  return end.toLocaleDateString('en-SG');
};

/** Extracts HH:MM from a datetime-local string like "2026-01-15T10:00" */
const fmtTime = (dt) => {
  if (!dt || typeof dt !== 'string') return null;
  const t = dt.split('T')[1];
  return t ? t.substring(0, 5) : null;
};

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const SG_DISTRICTS = [
  'Ang Mo Kio', 'Bedok', 'Bishan', 'Boon Lay', 'Bukit Batok',
  'Bukit Merah', 'Bukit Panjang', 'Bukit Timah', 'Central Area',
  'Choa Chu Kang', 'Clementi', 'Geylang', 'Hougang', 'Jurong East',
  'Jurong West', 'Kallang', 'Marine Parade', 'Novena', 'Pasir Ris',
  'Punggol', 'Queenstown', 'Sembawang', 'Sengkang', 'Serangoon',
  'Tampines', 'Toa Payoh', 'Woodlands', 'Yishun',
];

const EMPTY_RECURRENCE = {
  frequency: 'weekly',
  daysOfWeek: [],
  dayOfMonth: '',
  months: [],
};

const EMPTY_GROUP = {
  name: '', cohortName: '',
  facilitatorId: '', facilitatorName: '',
  startDateTime: '', endDateTime: '',
  capacity: '',
  meetingVenue: '', district: '',
  members: [],
  volunteers: [],
  isRecurring: false,
  recurrence: { ...EMPTY_RECURRENCE },
};

function TwoPanelMatchLayout({ leftTitle, leftItems, leftEmpty, leftRender, rightTitle, rightItems, rightEmpty, rightRender }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
      <div>
        <div className="bb-section-title" style={{ marginBottom: 12 }}>{leftTitle}</div>
        {leftItems.length === 0 ? (
          <div className="bb-empty-state">{leftEmpty}</div>
        ) : leftItems.map(leftRender)}
      </div>
      <div>
        <div className="bb-section-title" style={{ marginBottom: 12 }}>{rightTitle}</div>
        {rightItems.length === 0 ? (
          <div className="bb-empty-state">{rightEmpty}</div>
        ) : rightItems.map(rightRender)}
      </div>
    </div>
  );
}

export default function SupportNetwork({ orgId }) {
  const { currentUser } = useOrgAuth();
  const [activeTab, setActiveTab] = useState('careConnect');

  // Care & Connect
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [groupForm, setGroupForm] = useState(EMPTY_GROUP);
  const [groupSubmitting, setGroupSubmitting] = useState(false);

  // Dropdown data for New Support Group form
  const [facilitatorVols, setFacilitatorVols] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [allVolunteers, setAllVolunteers] = useState([]);
  const [memberPickId, setMemberPickId]         = useState('');
  const [volunteerPickId, setVolunteerPickId]   = useState('');

  // Edit group modal
  const [editingGroup, setEditingGroup]         = useState(null);
  const [editForm, setEditForm]                 = useState(EMPTY_GROUP);
  const [editSubmitting, setEditSubmitting]     = useState(false);
  const [editMemberPickId, setEditMemberPickId] = useState('');
  const [editVolPickId, setEditVolPickId]       = useState('');

  // Group Join Requests (admin)
  const [joinRequests, setJoinRequests]         = useState([]);
  const [joinReqLoading, setJoinReqLoading]     = useState(false);
  const [joinReqError, setJoinReqError]         = useState('');
  const [declineModal, setDeclineModal]         = useState(null); // { id, reason }
  const [processingReq, setProcessingReq]       = useState({});

  // Mentoring (Rebuild & Shine)
  const [mentoringPairs, setMentoringPairs] = useState([]);
  const [mentorRequests, setMentorRequests] = useState([]);
  const [mentoringLoading, setMentoringLoading] = useState(false);
  const [mentoringError, setMentoringError] = useState('');
  const [endMentorModal, setEndMentorModal] = useState(null);
  const [availableMentorsList, setAvailableMentorsList] = useState([]);
  const [matchMentor, setMatchMentor] = useState(null);
  const [matchingMentor, setMatchingMentor] = useState({});

  // Peer (Widow-to-Widow)
  const [peerConnections, setPeerConnections] = useState([]);
  const [peerRequests, setPeerRequests] = useState([]);
  const [peerLoading, setPeerLoading] = useState(false);
  const [peerError, setPeerError] = useState('');
  const [endPeerModal, setEndPeerModal] = useState(null);
  const [matchPeer, setMatchPeer] = useState(null);
  const [matchingPeer, setMatchingPeer] = useState({});
  const [availableBefriendersList, setAvailableBefriendersList] = useState([]);
  const [convHistoryConn, setConvHistoryConn] = useState(null);
  const [convHistoryMessages, setConvHistoryMessages] = useState([]);
  const [convHistoryLoading, setConvHistoryLoading] = useState(false);

  // Volunteers
  const [pendingVolunteers, setPendingVolunteers] = useState([]);
  const [approvedVolunteers, setApprovedVolunteers] = useState([]);
  const [volunteersLoading, setVolunteersLoading] = useState(false);
  const [volunteersError, setVolunteersError] = useState('');
  const [volUpdating, setVolUpdating] = useState({});

  // Concerns
  const [concerns, setConcerns] = useState([]);
  const [concernsLoading, setConcernsLoading] = useState(false);
  const [concernsError, setConcernsError] = useState('');
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [viewConcern, setViewConcern] = useState(null);

  useEffect(() => {
    if (activeTab === 'careConnect') loadGroups();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'rebuild') loadMentoring();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'widowToWidow') loadPeer();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'volunteers') loadVolunteers();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'concerns') loadConcerns();
  }, [activeTab, orgId]);

  const loadGroups = async () => {
    setGroupsLoading(true); setGroupsError('');
    setJoinReqLoading(true); setJoinReqError('');
    try {
      const [legacy, network, vols, members, joinReqs] = await Promise.all([
        getCareConnectGroups(orgId).catch(() => []),
        getSupportNetworkGroups(orgId).catch(() => []),
        getVolunteers(orgId, 'active').catch(() => []),
        getMembers(orgId).catch(() => []),
        getGroupJoinRequests(orgId, null).catch(() => []),
      ]);
      setJoinRequests(joinReqs);

      // Facilitators = active volunteers who have an approved 'Facilitator' role
      setFacilitatorVols(vols.filter(v => (v.volunteerRoles || []).includes('Facilitator')));
      setAllVolunteers(vols);
      setAllMembers(members.filter(m => m.status === 'active'));

      const normalised = network.map(g => ({
        ...g,
        _source: 'supportNetwork',
        enrolledCount: g.enrolledCount ?? (g.members || []).length,
      }));
      setGroups([...normalised, ...legacy]);
    }
    catch (e) { setGroupsError(e.message || 'Failed to load groups.'); setJoinReqError(e.message || ''); }
    finally { setGroupsLoading(false); setJoinReqLoading(false); }
  };

  const loadMentoring = async () => {
    setMentoringLoading(true); setMentoringError('');
    try {
      const [pairs, reqs, vols] = await Promise.all([
        getMentoringPairs(orgId),
        getPendingSupportRequests(orgId, 'rebuild_shine'),
        getVolunteers(orgId, 'active').catch(() => []),
      ]);
      setMentoringPairs(pairs || []);
      setMentorRequests(reqs || []);
      setAvailableMentorsList(
        (vols || []).filter(v => (v.volunteerRoles || []).includes('Mentor'))
      );
    } catch (e) { setMentoringError(e.message || 'Failed to load mentoring data.'); }
    finally { setMentoringLoading(false); }
  };

  const loadPeer = async () => {
    setPeerLoading(true); setPeerError('');
    try {
      const [conns, reqs, vols] = await Promise.all([
        getPeerConnections(orgId),
        getPendingSupportRequests(orgId, 'widow_lifeline'),
        getVolunteers(orgId, 'active').catch(() => []),
      ]);
      setPeerConnections(conns || []);
      setPeerRequests(reqs || []);
      setAvailableBefriendersList(
        (vols || []).filter(v => (v.volunteerRoles || []).includes('Befriender'))
      );
    } catch (e) { setPeerError(e.message || 'Failed to load peer data.'); }
    finally { setPeerLoading(false); }
  };

  const loadVolunteers = async () => {
    setVolunteersLoading(true); setVolunteersError('');
    try {
      const all = await getPendingVolunteerRoles(orgId) || [];
      setPendingVolunteers(all.filter(v => v.status === 'pending'));
      setApprovedVolunteers(all.filter(v => v.status === 'approved'));
    } catch (e) { setVolunteersError(e.message || 'Failed to load volunteers.'); }
    finally { setVolunteersLoading(false); }
  };

  const loadConcerns = async () => {
    setConcernsLoading(true); setConcernsError('');
    try { setConcerns(await getConcerns(orgId) || []); }
    catch (e) { setConcernsError(e.message || 'Failed to load concerns.'); }
    finally { setConcernsLoading(false); }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault(); setGroupSubmitting(true); setGroupsError('');
    try {
      const payload = {
        ...groupForm,
        facilitator: groupForm.facilitatorName,
        createdBy: currentUser.uid,
      };
      if (!groupForm.isRecurring) delete payload.recurrence;
      await createSupportNetworkGroup(orgId, payload);
      setShowAddGroup(false);
      setGroupForm(EMPTY_GROUP);
      setMemberPickId('');
      setVolunteerPickId('');
      await loadGroups();
    } catch (e) { setGroupsError(e.message || 'Failed to create group.'); }
    finally { setGroupSubmitting(false); }
  };

  const openEditGroup = (g) => {
    setEditForm({
      name:            g.name || '',
      cohortName:      g.cohortName || '',
      facilitatorId:   g.facilitatorId || '',
      facilitatorName: g.facilitatorName || g.facilitator || '',
      startDateTime:   g.startDateTime || '',
      endDateTime:     g.endDateTime || '',
      capacity:        g.capacity || '',
      meetingVenue:    g.meetingVenue || '',
      district:        g.district || '',
      members:         g.members || [],
      volunteers:      g.volunteers || [],
      isRecurring:     g.isRecurring || false,
      recurrence:      g.recurrence ? { ...EMPTY_RECURRENCE, ...g.recurrence } : { ...EMPTY_RECURRENCE },
    });
    setEditMemberPickId('');
    setEditVolPickId('');
    setEditingGroup(g);
  };

  const handleEditGroup = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    setGroupsError('');
    try {
      const payload = {
        ...editForm,
        facilitator: editForm.facilitatorName,
      };
      if (!editForm.isRecurring) delete payload.recurrence;
      await updateSupportNetworkGroup(orgId, editingGroup.id, payload);
      setEditingGroup(null);
      await loadGroups();
    } catch (err) {
      setGroupsError(err.message || 'Failed to update group.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleApproveJoinRequest = async (reqId) => {
    setProcessingReq(p => ({ ...p, [reqId]: 'approving' }));
    setJoinReqError('');
    try {
      await approveGroupJoinRequest(orgId, reqId, currentUser.uid, currentUser.displayName || 'Admin');
      await loadGroups();
    } catch (e) { setJoinReqError(e.message || 'Failed to approve request.'); }
    finally { setProcessingReq(p => ({ ...p, [reqId]: null })); }
  };

  const handleDeclineJoinRequest = async () => {
    if (!declineModal) return;
    setProcessingReq(p => ({ ...p, [declineModal.id]: 'declining' }));
    setJoinReqError('');
    try {
      await declineGroupJoinRequest(orgId, declineModal.id, currentUser.uid, currentUser.displayName || 'Admin', declineModal.reason);
      setDeclineModal(null);
      await loadGroups();
    } catch (e) { setJoinReqError(e.message || 'Failed to decline request.'); }
    finally { setProcessingReq(p => ({ ...p, [declineModal?.id]: null })); }
  };

  const handleCreateMentorPair = async (req, mentorId, mentorName) => {
    setMatchingMentor(p => ({ ...p, [req.id]: true }));
    try {
      await createMentoringPair(orgId, mentorId, mentorName, req.memberId, req.memberName);
      setMatchMentor(null);
      await loadMentoring();
    } catch (e) { setMentoringError(e.message || 'Failed to create mentoring pair.'); }
    finally { setMatchingMentor(p => ({ ...p, [req.id]: false })); }
  };

  const handleEndMentorPair = async () => {
    if (!endMentorModal.reason?.trim()) { setMentoringError('A reason is required to end the pair.'); return; }
    try {
      await endMentoringPair(orgId, endMentorModal.id, endMentorModal.reason, currentUser.uid);
      setEndMentorModal(null);
      await loadMentoring();
    } catch (e) { setMentoringError(e.message || 'Failed to end mentoring pair.'); }
  };

  const handleCreatePeerConnection = async (req, supporterId, supporterName) => {
    setMatchingPeer(p => ({ ...p, [req.id]: true }));
    try {
      const befriender = availableBefriendersList.find(b => b.id === supporterId);
      await createPeerConnection(orgId, supporterId, supporterName, req.memberId, req.memberName, req.id, befriender?.authUid || null);
      setMatchPeer(null);
      await loadPeer();
    } catch (e) { setPeerError(e.message || 'Failed to create peer connection.'); }
    finally { setMatchingPeer(p => ({ ...p, [req.id]: false })); }
  };

  const handleEndPeerConnection = async () => {
    if (!endPeerModal.reason?.trim()) { setPeerError('A reason is required to end the connection.'); return; }
    try {
      await endPeerConnection(orgId, endPeerModal.id, endPeerModal.reason, currentUser.uid);
      setEndPeerModal(null);
      await loadPeer();
    } catch (e) { setPeerError(e.message || 'Failed to end peer connection.'); }
  };

  const openConvHistory = async (conn) => {
    setConvHistoryConn(conn);
    setConvHistoryLoading(true);
    setConvHistoryMessages([]);
    try {
      const msgs = await getPeerMessages(orgId, conn.id);
      setConvHistoryMessages(msgs || []);
    } catch (e) {
      setPeerError(e.message || 'Failed to load conversation history.');
      setConvHistoryConn(null);
    } finally {
      setConvHistoryLoading(false);
    }
  };

  const handleApproveVolunteer = async (volunteerId, action) => {
    setVolUpdating(p => ({ ...p, [volunteerId]: true }));
    try {
      await approveVolunteerRole(orgId, volunteerId, action, currentUser.uid);
      await loadVolunteers();
    } catch (e) { setVolunteersError(e.message || 'Failed to update volunteer.'); }
    finally { setVolUpdating(p => ({ ...p, [volunteerId]: false })); }
  };

  const handleResolveConcern = async () => {
    try {
      await resolveConcern(orgId, resolveModal, resolveNotes, currentUser.uid);
      setResolveModal(null); setResolveNotes('');
      await loadConcerns();
    } catch (e) { setConcernsError(e.message || 'Failed to resolve concern.'); }
  };

  return (
    <div className="bb-editor">
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: activeTab === t.key ? '2px solid #6B46C1' : '2px solid transparent',
            color: activeTab === t.key ? '#6B46C1' : '#6B7280',
            fontWeight: activeTab === t.key ? 600 : 400, fontSize: 13
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── CARE & CONNECT ── */}
      {activeTab === 'careConnect' && (
        <div>
          <div className="bb-editor-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0 }}>Care & Connect — Group Management</h2>
              <p className="bb-editor-desc">Manage support cohorts and group sessions for members.</p>
            </div>
            <button className="bb-btn-primary" onClick={() => setShowAddGroup(v => !v)}>{showAddGroup ? 'Cancel' : '+ Create Group'}</button>
          </div>
          {groupsError && <div className="bb-alert bb-alert--error">{groupsError}</div>}
          {showAddGroup && (
            <form onSubmit={handleAddGroup} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div className="bb-section-title" style={{ marginBottom: 16 }}>New Support Group</div>

              {/* ── Group name & cohort ── */}
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Group Name <span className="bb-required">*</span></label>
                  <input className="bb-input" required value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Healing Together Cohort 4" />
                </div>
                <div className="bb-form-group">
                  <label>Cohort Name</label>
                  <input className="bb-input" value={groupForm.cohortName} onChange={e => setGroupForm(p => ({ ...p, cohortName: e.target.value }))} placeholder="e.g. Cohort 4 (Q2 2026)" />
                </div>
              </div>

              {/* ── Facilitator & Capacity ── */}
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Facilitator Name</label>
                  <select
                    className="bb-select"
                    value={groupForm.facilitatorId}
                    onChange={e => {
                      const vol = facilitatorVols.find(v => v.id === e.target.value);
                      setGroupForm(p => ({
                        ...p,
                        facilitatorId:   e.target.value,
                        facilitatorName: vol ? vol.displayName : '',
                      }));
                    }}
                  >
                    <option value="">— Select facilitator —</option>
                    {facilitatorVols.map(v => (
                      <option key={v.id} value={v.id}>{v.displayName}</option>
                    ))}
                    {facilitatorVols.length === 0 && (
                      <option disabled>No approved facilitators found</option>
                    )}
                  </select>
                </div>
                <div className="bb-form-group">
                  <label>Capacity</label>
                  <input className="bb-input" type="number" min={1} value={groupForm.capacity} onChange={e => setGroupForm(p => ({ ...p, capacity: e.target.value }))} placeholder="e.g. 12" />
                </div>
              </div>

              {/* ── Start & End Date/Time ── */}
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Start Date &amp; Time</label>
                  <input className="bb-input" type="datetime-local" value={groupForm.startDateTime} onChange={e => setGroupForm(p => ({ ...p, startDateTime: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>End Date &amp; Time</label>
                  <input className="bb-input" type="datetime-local" value={groupForm.endDateTime} onChange={e => setGroupForm(p => ({ ...p, endDateTime: e.target.value }))} />
                </div>
              </div>

              {/* ── Meeting Venue & District ── */}
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Meeting Venue</label>
                  <input className="bb-input" value={groupForm.meetingVenue} onChange={e => setGroupForm(p => ({ ...p, meetingVenue: e.target.value }))} placeholder="e.g. Bishan Community Club, Room 3" />
                </div>
                <div className="bb-form-group">
                  <label>District</label>
                  <select className="bb-select" value={groupForm.district} onChange={e => setGroupForm(p => ({ ...p, district: e.target.value }))}>
                    <option value="">— Select district —</option>
                    {SG_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Recurring ── */}
              <div className="bb-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={groupForm.isRecurring}
                    onChange={e => setGroupForm(p => ({ ...p, isRecurring: e.target.checked, recurrence: { ...EMPTY_RECURRENCE } }))}
                  />
                  <span style={{ fontWeight: 500 }}>Recurring session</span>
                </label>
              </div>

              {groupForm.isRecurring && (
                <div style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#3730A3', marginBottom: 12 }}>Recurrence Settings</div>

                  {/* Frequency selector */}
                  <div className="bb-form-group">
                    <label>Frequency</label>
                    <div style={{ display: 'flex', gap: 12 }}>
                      {['weekly', 'monthly', 'quarterly'].map(f => (
                        <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="recurrenceFreq"
                            value={f}
                            checked={groupForm.recurrence.frequency === f}
                            onChange={() => setGroupForm(p => ({
                              ...p,
                              recurrence: { ...EMPTY_RECURRENCE, frequency: f },
                            }))}
                          />
                          {f.charAt(0).toUpperCase() + f.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Weekly — days of week */}
                  {groupForm.recurrence.frequency === 'weekly' && (
                    <div className="bb-form-group">
                      <label>Day(s) of Week</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {DAYS_OF_WEEK.map((day, idx) => {
                          const checked = groupForm.recurrence.daysOfWeek.includes(idx);
                          return (
                            <label key={day} style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              background: checked ? '#6366F1' : '#fff',
                              color: checked ? '#fff' : '#374151',
                              border: '1px solid ' + (checked ? '#6366F1' : '#D1D5DB'),
                              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                            }}>
                              <input
                                type="checkbox"
                                style={{ display: 'none' }}
                                checked={checked}
                                onChange={() => setGroupForm(p => {
                                  const days = p.recurrence.daysOfWeek.includes(idx)
                                    ? p.recurrence.daysOfWeek.filter(d => d !== idx)
                                    : [...p.recurrence.daysOfWeek, idx].sort();
                                  return { ...p, recurrence: { ...p.recurrence, daysOfWeek: days } };
                                })}
                              />
                              {day}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Monthly — day of month */}
                  {groupForm.recurrence.frequency === 'monthly' && (
                    <div className="bb-form-group">
                      <label>Day of Month</label>
                      <input
                        className="bb-input"
                        type="number"
                        min={1}
                        max={31}
                        placeholder="e.g. 15"
                        value={groupForm.recurrence.dayOfMonth}
                        onChange={e => setGroupForm(p => ({ ...p, recurrence: { ...p.recurrence, dayOfMonth: e.target.value } }))}
                        style={{ maxWidth: 120 }}
                      />
                      <span style={{ fontSize: 12, color: '#6B7280', marginTop: 4, display: 'block' }}>Repeats on this day each month</span>
                    </div>
                  )}

                  {/* Quarterly — select months */}
                  {groupForm.recurrence.frequency === 'quarterly' && (
                    <div className="bb-form-group">
                      <label>Months</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {MONTHS.map((month, idx) => {
                          const checked = groupForm.recurrence.months.includes(idx + 1);
                          return (
                            <label key={month} style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              background: checked ? '#6366F1' : '#fff',
                              color: checked ? '#fff' : '#374151',
                              border: '1px solid ' + (checked ? '#6366F1' : '#D1D5DB'),
                              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                            }}>
                              <input
                                type="checkbox"
                                style={{ display: 'none' }}
                                checked={checked}
                                onChange={() => setGroupForm(p => {
                                  const months = p.recurrence.months.includes(idx + 1)
                                    ? p.recurrence.months.filter(m => m !== idx + 1)
                                    : [...p.recurrence.months, idx + 1].sort((a, b) => a - b);
                                  return { ...p, recurrence: { ...p.recurrence, months } };
                                })}
                              />
                              {month}
                            </label>
                          );
                        })}
                      </div>
                      <span style={{ fontSize: 12, color: '#6B7280', marginTop: 4, display: 'block' }}>Select the 4 months this group meets (e.g. Jan, Apr, Jul, Oct)</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Add Members ── */}
              <div className="bb-form-group">
                <label>Add Member</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="bb-select"
                    value={memberPickId}
                    onChange={e => setMemberPickId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">— Select member —</option>
                    {allMembers
                      .filter(m => !groupForm.members.some(gm => gm.memberId === m.id))
                      .map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)
                    }
                  </select>
                  <button
                    type="button"
                    className="bb-btn-outline-sm"
                    disabled={!memberPickId}
                    onClick={() => {
                      const m = allMembers.find(x => x.id === memberPickId);
                      if (!m) return;
                      setGroupForm(p => ({ ...p, members: [...p.members, { memberId: m.id, memberName: m.displayName }] }));
                      setMemberPickId('');
                    }}
                  >Add</button>
                </div>
                {groupForm.members.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {groupForm.members.map(m => (
                      <span key={m.memberId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#DBEAFE', color: '#1E40AF', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                        {m.memberName}
                        <button type="button" onClick={() => setGroupForm(p => ({ ...p, members: p.members.filter(x => x.memberId !== m.memberId) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Add Volunteers ── */}
              <div className="bb-form-group">
                <label>Add Volunteer</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="bb-select"
                    value={volunteerPickId}
                    onChange={e => setVolunteerPickId(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">— Select volunteer —</option>
                    {allVolunteers
                      .filter(v => !groupForm.volunteers.some(gv => gv.volunteerId === v.id))
                      .map(v => <option key={v.id} value={v.id}>{v.displayName}</option>)
                    }
                  </select>
                  <button
                    type="button"
                    className="bb-btn-outline-sm"
                    disabled={!volunteerPickId}
                    onClick={() => {
                      const v = allVolunteers.find(x => x.id === volunteerPickId);
                      if (!v) return;
                      setGroupForm(p => ({ ...p, volunteers: [...p.volunteers, { volunteerId: v.id, volunteerName: v.displayName }] }));
                      setVolunteerPickId('');
                    }}
                  >Add</button>
                </div>
                {groupForm.volunteers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {groupForm.volunteers.map(v => (
                      <span key={v.volunteerId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#D1FAE5', color: '#065F46', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                        {v.volunteerName}
                        <button type="button" onClick={() => setGroupForm(p => ({ ...p, volunteers: p.volunteers.filter(x => x.volunteerId !== v.volunteerId) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="bb-editor-actions">
                <button className="bb-btn-primary" type="submit" disabled={groupSubmitting}>{groupSubmitting ? 'Creating…' : 'Create Group'}</button>
                <button className="bb-btn-outline-sm" type="button" onClick={() => { setShowAddGroup(false); setGroupForm(EMPTY_GROUP); setMemberPickId(''); setVolunteerPickId(''); }}>Cancel</button>
              </div>
            </form>
          )}
          {/* ── Edit Group Modal ── */}
          {editingGroup && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={e => e.target === e.currentTarget && setEditingGroup(null)}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>Edit Group — {editingGroup.name}</h3>
                  <button className="bb-btn-outline-sm" onClick={() => setEditingGroup(null)}>✕</button>
                </div>

                <form onSubmit={handleEditGroup}>
                  <div className="bb-form-row">
                    <div className="bb-form-group">
                      <label>Group Name <span className="bb-required">*</span></label>
                      <input className="bb-input" required value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="bb-form-group">
                      <label>Cohort Name</label>
                      <input className="bb-input" value={editForm.cohortName} onChange={e => setEditForm(p => ({ ...p, cohortName: e.target.value }))} />
                    </div>
                  </div>

                  <div className="bb-form-row">
                    <div className="bb-form-group">
                      <label>Facilitator Name</label>
                      <select className="bb-select" value={editForm.facilitatorId}
                        onChange={e => {
                          const vol = facilitatorVols.find(v => v.id === e.target.value);
                          setEditForm(p => ({ ...p, facilitatorId: e.target.value, facilitatorName: vol ? vol.displayName : '' }));
                        }}>
                        <option value="">— Select facilitator —</option>
                        {facilitatorVols.map(v => <option key={v.id} value={v.id}>{v.displayName}</option>)}
                        {facilitatorVols.length === 0 && <option disabled>No approved facilitators found</option>}
                      </select>
                    </div>
                    <div className="bb-form-group">
                      <label>Capacity</label>
                      <input className="bb-input" type="number" min={1} value={editForm.capacity} onChange={e => setEditForm(p => ({ ...p, capacity: e.target.value }))} placeholder="e.g. 12" />
                    </div>
                  </div>

                  <div className="bb-form-row">
                    <div className="bb-form-group">
                      <label>Start Date &amp; Time</label>
                      <input className="bb-input" type="datetime-local" value={editForm.startDateTime} onChange={e => setEditForm(p => ({ ...p, startDateTime: e.target.value }))} />
                    </div>
                    <div className="bb-form-group">
                      <label>End Date &amp; Time</label>
                      <input className="bb-input" type="datetime-local" value={editForm.endDateTime} onChange={e => setEditForm(p => ({ ...p, endDateTime: e.target.value }))} />
                    </div>
                  </div>

                  {/* ── Meeting Venue & District ── */}
                  <div className="bb-form-row">
                    <div className="bb-form-group">
                      <label>Meeting Venue</label>
                      <input className="bb-input" value={editForm.meetingVenue} onChange={e => setEditForm(p => ({ ...p, meetingVenue: e.target.value }))} placeholder="e.g. Bishan Community Club, Room 3" />
                    </div>
                    <div className="bb-form-group">
                      <label>District</label>
                      <select className="bb-select" value={editForm.district} onChange={e => setEditForm(p => ({ ...p, district: e.target.value }))}>
                        <option value="">— Select district —</option>
                        {SG_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="bb-form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                      <input type="checkbox" checked={editForm.isRecurring}
                        onChange={e => setEditForm(p => ({ ...p, isRecurring: e.target.checked, recurrence: { ...EMPTY_RECURRENCE } }))} />
                      <span style={{ fontWeight: 500 }}>Recurring session</span>
                    </label>
                  </div>

                  {editForm.isRecurring && (
                    <div style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#3730A3', marginBottom: 12 }}>Recurrence Settings</div>
                      <div className="bb-form-group">
                        <label>Frequency</label>
                        <div style={{ display: 'flex', gap: 12 }}>
                          {['weekly', 'monthly', 'quarterly'].map(f => (
                            <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                              <input type="radio" name="editRecurrenceFreq" value={f}
                                checked={editForm.recurrence.frequency === f}
                                onChange={() => setEditForm(p => ({ ...p, recurrence: { ...EMPTY_RECURRENCE, frequency: f } }))} />
                              {f.charAt(0).toUpperCase() + f.slice(1)}
                            </label>
                          ))}
                        </div>
                      </div>
                      {editForm.recurrence.frequency === 'weekly' && (
                        <div className="bb-form-group">
                          <label>Day(s) of Week</label>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {DAYS_OF_WEEK.map((day, idx) => {
                              const checked = editForm.recurrence.daysOfWeek.includes(idx);
                              return (
                                <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 4, background: checked ? '#6366F1' : '#fff', color: checked ? '#fff' : '#374151', border: '1px solid ' + (checked ? '#6366F1' : '#D1D5DB'), borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                                  <input type="checkbox" style={{ display: 'none' }} checked={checked}
                                    onChange={() => setEditForm(p => {
                                      const days = p.recurrence.daysOfWeek.includes(idx) ? p.recurrence.daysOfWeek.filter(d => d !== idx) : [...p.recurrence.daysOfWeek, idx].sort();
                                      return { ...p, recurrence: { ...p.recurrence, daysOfWeek: days } };
                                    })} />
                                  {day}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {editForm.recurrence.frequency === 'monthly' && (
                        <div className="bb-form-group">
                          <label>Day of Month</label>
                          <input className="bb-input" type="number" min={1} max={31} placeholder="e.g. 15" value={editForm.recurrence.dayOfMonth}
                            onChange={e => setEditForm(p => ({ ...p, recurrence: { ...p.recurrence, dayOfMonth: e.target.value } }))} style={{ maxWidth: 120 }} />
                        </div>
                      )}
                      {editForm.recurrence.frequency === 'quarterly' && (
                        <div className="bb-form-group">
                          <label>Months</label>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {MONTHS.map((month, idx) => {
                              const checked = editForm.recurrence.months.includes(idx + 1);
                              return (
                                <label key={month} style={{ display: 'flex', alignItems: 'center', gap: 4, background: checked ? '#6366F1' : '#fff', color: checked ? '#fff' : '#374151', border: '1px solid ' + (checked ? '#6366F1' : '#D1D5DB'), borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
                                  <input type="checkbox" style={{ display: 'none' }} checked={checked}
                                    onChange={() => setEditForm(p => {
                                      const months = p.recurrence.months.includes(idx + 1) ? p.recurrence.months.filter(m => m !== idx + 1) : [...p.recurrence.months, idx + 1].sort((a, b) => a - b);
                                      return { ...p, recurrence: { ...p.recurrence, months } };
                                    })} />
                                  {month}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Members */}
                  <div className="bb-form-group">
                    <label>Add Member</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select className="bb-select" value={editMemberPickId} onChange={e => setEditMemberPickId(e.target.value)} style={{ flex: 1 }}>
                        <option value="">— Select member —</option>
                        {allMembers.filter(m => !editForm.members.some(gm => gm.memberId === m.id)).map(m => <option key={m.id} value={m.id}>{m.displayName}</option>)}
                      </select>
                      <button type="button" className="bb-btn-outline-sm" disabled={!editMemberPickId}
                        onClick={() => {
                          const m = allMembers.find(x => x.id === editMemberPickId);
                          if (!m) return;
                          setEditForm(p => ({ ...p, members: [...p.members, { memberId: m.id, memberName: m.displayName }] }));
                          setEditMemberPickId('');
                        }}>Add</button>
                    </div>
                    {editForm.members.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {editForm.members.map(m => (
                          <span key={m.memberId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#DBEAFE', color: '#1E40AF', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                            {m.memberName}
                            <button type="button" onClick={() => setEditForm(p => ({ ...p, members: p.members.filter(x => x.memberId !== m.memberId) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF', fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Volunteers */}
                  <div className="bb-form-group">
                    <label>Add Volunteer</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select className="bb-select" value={editVolPickId} onChange={e => setEditVolPickId(e.target.value)} style={{ flex: 1 }}>
                        <option value="">— Select volunteer —</option>
                        {allVolunteers.filter(v => !editForm.volunteers.some(gv => gv.volunteerId === v.id)).map(v => <option key={v.id} value={v.id}>{v.displayName}</option>)}
                      </select>
                      <button type="button" className="bb-btn-outline-sm" disabled={!editVolPickId}
                        onClick={() => {
                          const v = allVolunteers.find(x => x.id === editVolPickId);
                          if (!v) return;
                          setEditForm(p => ({ ...p, volunteers: [...p.volunteers, { volunteerId: v.id, volunteerName: v.displayName }] }));
                          setEditVolPickId('');
                        }}>Add</button>
                    </div>
                    {editForm.volunteers.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {editForm.volunteers.map(v => (
                          <span key={v.volunteerId} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#D1FAE5', color: '#065F46', borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                            {v.volunteerName}
                            <button type="button" onClick={() => setEditForm(p => ({ ...p, volunteers: p.volunteers.filter(x => x.volunteerId !== v.volunteerId) }))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bb-editor-actions">
                    <button className="bb-btn-primary" type="submit" disabled={editSubmitting}>{editSubmitting ? 'Saving…' : 'Save Changes'}</button>
                    <button className="bb-btn-outline-sm" type="button" onClick={() => setEditingGroup(null)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── Decline Request Modal ── */}
          {declineModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={e => e.target === e.currentTarget && setDeclineModal(null)}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ margin: '0 0 16px' }}>Decline Request</h3>
                <div className="bb-form-group">
                  <label>Reason (optional)</label>
                  <textarea className="bb-textarea" rows={3} value={declineModal.reason}
                    onChange={e => setDeclineModal(p => ({ ...p, reason: e.target.value }))}
                    placeholder="Let the member know why their request was not approved…" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={handleDeclineJoinRequest}
                    disabled={processingReq[declineModal.id] === 'declining'}>
                    {processingReq[declineModal.id] === 'declining' ? 'Declining…' : 'Decline Request'}
                  </button>
                  <button className="bb-btn-outline-sm" onClick={() => setDeclineModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {groupsLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={8}>Loading groups…</td></tr></tbody></table>
          ) : groups.length === 0 ? (
            <div className="bb-empty-state">No support groups created yet.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Group Name</th>
                  <th>Facilitator / Volunteer</th>
                  <th>Members</th>
                  <th>Enrolled</th>
                  <th>Recurrence &amp; Time</th>
                  <th>Meeting Venue</th>
                  <th>District</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => {
                  const rec = g.isRecurring && g.recurrence;
                  const recLabel = (() => {
                    if (!rec) return null;
                    if (rec.frequency === 'weekly') {
                      const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                      const days = (rec.daysOfWeek || []).map(d => dayNames[d]).join(', ');
                      return `Weekly${days ? ' · ' + days : ''}`;
                    }
                    if (rec.frequency === 'monthly') return `Monthly · day ${rec.dayOfMonth}`;
                    if (rec.frequency === 'quarterly') {
                      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const ms = (rec.months || []).map(m => monthNames[m - 1]).join(', ');
                      return `Quarterly${ms ? ' · ' + ms : ''}`;
                    }
                    return rec.frequency;
                  })();

                  const startTime = fmtTime(g.startDateTime);
                  const endTime   = fmtTime(g.endDateTime);
                  const timeRange = startTime ? `${startTime}${endTime ? ' – ' + endTime : ''}` : null;

                  return (
                    <tr key={g.id}>
                      <td>
                        <strong>{g.name}</strong>
                        {g.cohortName && <span style={{ color: '#6B7280', fontSize: 12 }}> — {g.cohortName}</span>}
                        {g._source === 'supportNetwork' && <span className="bb-badge bb-badge--purple" style={{ fontSize: 10, marginLeft: 6 }}>Support Network</span>}
                      </td>
                      <td>
                        {g.volunteer ? (
                          <span>
                            {g.volunteer.name}
                            <span style={{ color: '#6B7280', fontSize: 11, marginLeft: 4 }}>({g.volunteer.role})</span>
                          </span>
                        ) : (g.facilitatorName || g.facilitator || '—')}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {g.members && g.members.length > 0 ? (
                          <ul style={{ margin: 0, padding: '0 0 0 14px' }}>
                            {g.members.map((m, i) => <li key={i}>{m.memberName}</li>)}
                          </ul>
                        ) : '—'}
                      </td>
                      <td>{g.enrolledCount || 0}</td>
                      <td>
                        {recLabel ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              background: rec.frequency === 'weekly' ? '#DBEAFE' : '#D1FAE5',
                              color:      rec.frequency === 'weekly' ? '#1E40AF' : '#065F46',
                              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                            }}>
                              🔁 {recLabel}
                            </span>
                            {timeRange && (
                              <span style={{ fontSize: 11, color: '#6B7280' }}>🕐 {timeRange}</span>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span style={{ color: '#9CA3AF', fontSize: 12 }}>One-off</span>
                            {timeRange && (
                              <span style={{ fontSize: 11, color: '#6B7280' }}>🕐 {timeRange}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{g.meetingVenue || '—'}</td>
                      <td style={{ fontSize: 12 }}>{g.district || '—'}</td>
                      <td>
                        <button className="bb-btn-outline-sm" onClick={() => openEditGroup(g)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── CONNECT GROUP REQUESTS ── */}
      {activeTab === 'careConnect' && (
        <div style={{ marginTop: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div className="bb-section-title" style={{ margin: 0, paddingLeft: '3ch' }}>
                Connect Group Requests
                {joinRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span style={{ marginLeft: 8, background: '#EDE9FE', color: '#6D28D9', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                    {joinRequests.filter(r => r.status === 'pending').length} pending
                  </span>
                )}
              </div>
              <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0', paddingLeft: '3ch' }}>Members who have requested to join a specific group. Approve to automatically enrol them.</p>
            </div>
          </div>
          {joinReqError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{joinReqError}</div>}
          {joinReqLoading ? (
            <div className="bb-empty-state">Loading requests…</div>
          ) : joinRequests.length === 0 ? (
            <div className="bb-empty-state">No group join requests yet.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Requested Group</th>
                  <th>Requested On</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {joinRequests.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.memberName}</strong></td>
                    <td>{r.groupName}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(r.requestedAt)}</td>
                    <td>
                      {r.status === 'pending' && <span className="bb-badge bb-badge--gray" style={{ background: '#FEF3C7', color: '#92400E' }}>Pending</span>}
                      {r.status === 'approved' && <span className="bb-badge bb-badge--green">Approved</span>}
                      {r.status === 'declined' && <span className="bb-badge" style={{ background: '#FEE2E2', color: '#991B1B' }}>Declined</span>}
                    </td>
                    <td>
                      {r.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="bb-btn-primary"
                            style={{ fontSize: 12, padding: '4px 12px' }}
                            disabled={!!processingReq[r.id]}
                            onClick={() => handleApproveJoinRequest(r.id)}
                          >
                            {processingReq[r.id] === 'approving' ? 'Approving…' : 'Approve'}
                          </button>
                          <button
                            className="bb-btn-danger-sm"
                            disabled={!!processingReq[r.id]}
                            onClick={() => setDeclineModal({ id: r.id, reason: '' })}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                      {r.status === 'declined' && r.declineReason && (
                        <span style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic' }}>{r.declineReason}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── REBUILD & SHINE (MENTORING) ── */}
      {activeTab === 'rebuild' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Rebuild & Shine — Mentor Matching</h2>
            <p className="bb-editor-desc">Match members with mentors and track active mentoring relationships.</p>
          </div>
          {mentoringError && <div className="bb-alert bb-alert--error">{mentoringError}</div>}

          {endMentorModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>End Mentoring Pair</h3>
                <div className="bb-form-group">
                  <label>Reason <span className="bb-required">*</span></label>
                  <textarea className="bb-textarea" rows={3} value={endMentorModal.reason || ''} onChange={e => setEndMentorModal(p => ({ ...p, reason: e.target.value }))} placeholder="Why is this mentoring relationship ending?" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={handleEndMentorPair}>End Pair</button>
                  <button className="bb-btn-outline-sm" onClick={() => setEndMentorModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {mentoringLoading ? (
            <div className="bb-loading-row" style={{ padding: 24, textAlign: 'center' }}>Loading mentoring data…</div>
          ) : (
            <>
              {/* ── Active Mentoring Pairs Table ── */}
              <div className="bb-section-title" style={{ marginBottom: 12, paddingLeft: '1.5em' }}>Active Mentoring Pairs</div>
              {mentoringPairs.filter(p => p.status !== 'ended').length === 0 ? (
                <div className="bb-empty-state" style={{ marginBottom: 32 }}>No active mentoring pairs.</div>
              ) : (
                <table className="bb-table" style={{ marginBottom: 32 }}>
                  <thead>
                    <tr>
                      <th>Mentor Name</th>
                      <th>Member Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mentoringPairs.filter(p => p.status !== 'ended').map(pair => {
                      const startTs = pair.startDate || pair.createdAt;
                      return (
                        <tr key={pair.id}>
                          <td>{pair.mentorName || pair.mentorId}</td>
                          <td>{pair.menteeName || pair.menteeId}</td>
                          <td>{fmtDate(startTs)}</td>
                          <td>{addSixMonths(startTs)}</td>
                          <td>
                            <button className="bb-btn-danger-sm" onClick={() => setEndMentorModal({ id: pair.id, reason: '' })}>
                              End Pair
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* ── Available Mentors & Mentoring Requests ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

                {/* Available Mentors */}
                <div>
                  <div className="bb-section-title" style={{ marginBottom: 12, paddingLeft: '1.5em' }}>
                    Available Mentors
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#9CA3AF', marginLeft: 8 }}>from Volunteer Register</span>
                  </div>
                  {availableMentorsList.length === 0 ? (
                    <div className="bb-empty-state">No approved mentors. Add mentors via Volunteer Register.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {availableMentorsList.map(m => (
                        <div key={m.id} className="bb-card" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong style={{ fontSize: 14 }}>{m.displayName}</strong>
                            {m.bio && (
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                {m.bio.slice(0, 80)}{m.bio.length > 80 ? '…' : ''}
                              </div>
                            )}
                          </div>
                          <span className="bb-badge bb-badge--green" style={{ fontSize: 11 }}>Mentor</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mentoring Requests */}
                <div>
                  <div className="bb-section-title" style={{ marginBottom: 12 }}>
                    Mentoring Requests
                    {mentorRequests.length > 0 && (
                      <span style={{ marginLeft: 8, background: '#EDE9FE', color: '#6D28D9', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                        {mentorRequests.length}
                      </span>
                    )}
                  </div>
                  {mentorRequests.length === 0 ? (
                    <div className="bb-empty-state">No pending mentoring requests.</div>
                  ) : mentorRequests.map(req => (
                    <div key={req.id} className="bb-card" style={{ marginBottom: 12, padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{req.memberName || req.memberId}</strong>
                          <div style={{ fontSize: 12, color: '#6B7280' }}>Requested {fmtDate(req.createdAt)}</div>
                          {req.notes && <div style={{ fontSize: 12, marginTop: 4, color: '#374151' }}>{req.notes}</div>}
                        </div>
                        <button
                          className="bb-btn-sm"
                          onClick={() => setMatchMentor(matchMentor === req.id ? null : req.id)}
                        >
                          {matchMentor === req.id ? 'Cancel' : 'Match'}
                        </button>
                      </div>
                      {matchMentor === req.id && (
                        <div style={{ marginTop: 12, background: '#F9FAFB', borderRadius: 6, padding: 12 }}>
                          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>Select a mentor to pair with {req.memberName}:</div>
                          {availableMentorsList.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#6B7280' }}>No approved mentors available.</div>
                          ) : availableMentorsList.map(m => (
                            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <span style={{ fontSize: 13 }}>{m.displayName}</span>
                              <button
                                className="bb-btn-primary"
                                style={{ fontSize: 12, padding: '4px 12px' }}
                                disabled={matchingMentor[req.id]}
                                onClick={() => handleCreateMentorPair(req, m.id, m.displayName)}
                              >
                                {matchingMentor[req.id] ? '…' : 'Create Pair'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

              </div>
            </>
          )}
        </div>
      )}

      {/* ── WIDOW-TO-WIDOW ── */}
      {activeTab === 'widowToWidow' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Widow-to-Widow — Peer Support</h2>
            <p className="bb-editor-desc">Match members with Befriender volunteers for shared-experience peer support.</p>
          </div>
          {peerError && <div className="bb-alert bb-alert--error">{peerError}</div>}

          {/* ── End Connection Modal ── */}
          {endPeerModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>End Peer Connection</h3>
                <div className="bb-form-group">
                  <label>Reason <span className="bb-required">*</span></label>
                  <textarea className="bb-textarea" rows={3} value={endPeerModal.reason || ''} onChange={e => setEndPeerModal(p => ({ ...p, reason: e.target.value }))} placeholder="Why is this connection ending?" />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-danger-sm" style={{ padding: '8px 20px' }} onClick={handleEndPeerConnection}>End Connection</button>
                  <button className="bb-btn-outline-sm" onClick={() => setEndPeerModal(null)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* ── Conversation History Modal ── */}
          {convHistoryConn && (
            <div
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={e => e.target === e.currentTarget && setConvHistoryConn(null)}
            >
              <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: '100%', maxWidth: 620, maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
                {/* Modal header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>Conversation History</h3>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                      <strong>{convHistoryConn.supporterName}</strong> &harr; <strong>{convHistoryConn.memberName}</strong>
                      <span style={{ marginLeft: 10, fontSize: 11, color: '#9CA3AF' }}>Connection started {fmtDate(convHistoryConn.startDate || convHistoryConn.createdAt)}</span>
                    </div>
                  </div>
                  <button className="bb-btn-outline-sm" onClick={() => setConvHistoryConn(null)}>✕</button>
                </div>

                {/* Message thread */}
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, background: '#FAFAFA' }}>
                  {convHistoryLoading ? (
                    <div style={{ textAlign: 'center', color: '#6B7280', padding: 32 }}>Loading messages…</div>
                  ) : convHistoryMessages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
                      No messages yet in this conversation.
                    </div>
                  ) : convHistoryMessages.map((msg, i) => {
                    const isVolunteer = msg.senderRole === 'volunteer';
                    return (
                      <div key={msg.id || i} style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: isVolunteer ? 'flex-end' : 'flex-start' }}>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 3 }}>
                          {msg.senderName}
                          <span style={{ marginLeft: 6, textTransform: 'capitalize', background: isVolunteer ? '#EDE9FE' : '#E0F2FE', color: isVolunteer ? '#5B21B6' : '#0369A1', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>
                            {msg.senderRole}
                          </span>
                          <span style={{ marginLeft: 6 }}>· {fmtDate(msg.createdAt)}</span>
                        </div>
                        <div style={{
                          background: isVolunteer ? '#EDE9FE' : '#F3F4F6',
                          color: isVolunteer ? '#3B0764' : '#111827',
                          borderRadius: isVolunteer ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                          padding: '9px 14px', maxWidth: '78%', fontSize: 13.5, lineHeight: 1.5,
                        }}>
                          {msg.text}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Admin notice footer */}
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, fontSize: 12, color: '#92400E' }}>
                  Admin view — read-only log. Members and volunteers send messages from their respective portals.
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="bb-btn-outline-sm" onClick={() => setConvHistoryConn(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {peerLoading ? (
            <div className="bb-loading-row" style={{ padding: 24, textAlign: 'center' }}>Loading peer support data…</div>
          ) : (
            <>
              {/* ── Widow-to-Widow Requests ── */}
              <div style={{ marginBottom: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, paddingLeft: '3ch' }}>
                  <div className="bb-section-title" style={{ margin: 0 }}>Widow-to-Widow Requests</div>
                  {peerRequests.length > 0 && (
                    <span style={{ background: '#EDE9FE', color: '#6D28D9', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                      {peerRequests.length} pending
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px', paddingLeft: '3ch' }}>
                  Members who have requested a Widow-to-Widow Befriender connection. Assign a Befriender to notify both parties and open secure in-app messaging.
                </p>
                {peerRequests.length === 0 ? (
                  <div className="bb-empty-state">No pending Widow-to-Widow requests.</div>
                ) : (
                  <table className="bb-table">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Requested On</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {peerRequests.map(req => (
                        <tr key={req.id}>
                          <td><strong>{req.memberName || req.memberId}</strong></td>
                          <td style={{ fontSize: 12 }}>{fmtDate(req.createdAt)}</td>
                          <td>
                            <span className="bb-badge" style={{ background: '#FEF3C7', color: '#92400E', fontSize: 11 }}>
                              Pending Match
                            </span>
                          </td>
                          <td>
                            {matchPeer === req.id ? (
                              <div style={{ minWidth: 280 }}>
                                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                                  Select a Befriender for <strong>{req.memberName}</strong>:
                                </div>
                                {availableBefriendersList.length === 0 ? (
                                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>
                                    No active Befrienders found. Register Befrienders in the Volunteer Register tab.
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                                    {availableBefriendersList.map(b => (
                                      <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px' }}>
                                        <div>
                                          <span style={{ fontSize: 13, fontWeight: 500 }}>{b.displayName}</span>
                                          {b.bio && (
                                            <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>
                                              {b.bio.slice(0, 50)}{b.bio.length > 50 ? '…' : ''}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          className="bb-btn-primary"
                                          style={{ fontSize: 11, padding: '4px 12px', marginLeft: 8, flexShrink: 0 }}
                                          disabled={!!matchingPeer[req.id]}
                                          onClick={() => handleCreatePeerConnection(req, b.id, b.displayName)}
                                        >
                                          {matchingPeer[req.id] ? '…' : 'Assign'}
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <button
                                  className="bb-btn-outline-sm"
                                  style={{ fontSize: 11 }}
                                  onClick={() => setMatchPeer(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                className="bb-btn-primary"
                                style={{ fontSize: 12, padding: '5px 14px' }}
                                onClick={() => setMatchPeer(req.id)}
                              >
                                Assign Befriender
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={{ borderTop: '1px solid #E5E7EB', marginBottom: 32 }} />

              {/* ── Active Peer Support Connections ── */}
              <div className="bb-editor-header" style={{ marginBottom: 0 }}>
                <h2 style={{ margin: 0 }}>Widow-to-Widow — Peer Support</h2>
                <p className="bb-editor-desc" style={{ margin: '4px 0 16px' }}>Match members with Befriender volunteers for shared-experience peer support.</p>
              </div>
              <div className="bb-section-title" style={{ marginBottom: 12, paddingLeft: '3ch' }}>Active Peer Support Connections</div>
              {peerConnections.filter(c => c.status !== 'ended').length === 0 ? (
                <div className="bb-empty-state" style={{ marginBottom: 32 }}>No active peer support connections.</div>
              ) : (
                <table className="bb-table" style={{ marginBottom: 32 }}>
                  <thead>
                    <tr>
                      <th>Peer Supporter</th>
                      <th>Member Name</th>
                      <th>Start Date and Time</th>
                      <th>End Date and Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {peerConnections.filter(c => c.status !== 'ended').map(conn => {
                      const startTs = conn.startDate || conn.createdAt;
                      return (
                        <tr key={conn.id}>
                          <td>{conn.supporterName || conn.peerName || conn.supporterId}</td>
                          <td>{conn.memberName || conn.memberId}</td>
                          <td>{fmtDateTime(startTs)}</td>
                          <td>{fmtDateTime(conn.endDate)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                className="bb-btn-sm"
                                style={{ fontSize: 12 }}
                                onClick={() => openConvHistory(conn)}
                              >
                                View Conversation History
                              </button>
                              <button
                                className="bb-btn-danger-sm"
                                onClick={() => setEndPeerModal({ id: conn.id, reason: '' })}
                              >
                                End Connection
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

            </>
          )}
        </div>
      )}

      {/* ── VOLUNTEER REGISTER ── */}
      {activeTab === 'volunteers' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Volunteer Register</h2>
            <p className="bb-editor-desc">Review and approve volunteer role applications.</p>
          </div>
          {volunteersError && <div className="bb-alert bb-alert--error">{volunteersError}</div>}
          <div className="bb-section-title" style={{ marginBottom: 12 }}>Pending Applications</div>
          {volunteersLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading volunteers…</td></tr></tbody></table>
          ) : pendingVolunteers.length === 0 ? (
            <div className="bb-empty-state" style={{ marginBottom: 32 }}>No pending volunteer applications.</div>
          ) : (
            <table className="bb-table" style={{ marginBottom: 32 }}>
              <thead><tr><th>Name</th><th>Role Type</th><th>Bio Preview</th><th>Applied</th><th>Actions</th></tr></thead>
              <tbody>
                {pendingVolunteers.map(v => (
                  <tr key={v.id}>
                    <td>{v.volunteerName}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(v.type || '').replace('_', ' ')}</td>
                    <td style={{ fontSize: 12, color: '#6B7280', maxWidth: 200 }}>{v.bio ? v.bio.slice(0, 100) + (v.bio.length > 100 ? '…' : '') : '—'}</td>
                    <td>{fmtDate(v.appliedAt || v.createdAt)}</td>
                    <td style={{ display: 'flex', gap: 4 }}>
                      <button className="bb-btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={volUpdating[v.id]} onClick={() => handleApproveVolunteer(v.id, 'approved')}>Approve</button>
                      <button className="bb-btn-danger-sm" disabled={volUpdating[v.id]} onClick={() => handleApproveVolunteer(v.id, 'rejected')}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="bb-section-title" style={{ marginBottom: 12 }}>Approved Volunteers</div>
          {approvedVolunteers.length === 0 ? (
            <div className="bb-empty-state">No approved volunteers yet.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Name</th><th>Role Type</th><th>Availability</th><th>Approved</th></tr></thead>
              <tbody>
                {approvedVolunteers.map(v => (
                  <tr key={v.id}>
                    <td>{v.volunteerName}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(v.type || '').replace('_', ' ')}</td>
                    <td>{safeStr(v.availability) || '—'}</td>
                    <td>{fmtDate(v.approvedAt || v.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── CONCERNS LOG ── */}
      {activeTab === 'concerns' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Concerns Log</h2>
            <p className="bb-editor-desc">Track and resolve flagged concerns from members, volunteers or staff.</p>
          </div>
          {concernsError && <div className="bb-alert bb-alert--error">{concernsError}</div>}

          {/* View Details Modal */}
          {viewConcern && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 520, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Concern Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '10px 16px', fontSize: 14, marginBottom: 16 }}>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>Reported By</span>
                  <span>{safeStr(viewConcern.reporterName) || safeStr(viewConcern.reporterId) || '—'}{safeStr(viewConcern.reporterRole) ? <span style={{ color: '#6B7280', fontSize: 12, marginLeft: 6 }}>({safeStr(viewConcern.reporterRole)})</span> : ''}</span>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>Subject</span>
                  <span>{safeStr(viewConcern.subjectName) || safeStr(viewConcern.subjectId) || '—'}</span>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>Type</span>
                  <span style={{ textTransform: 'capitalize' }}>{(safeStr(viewConcern.concernType) || '—').replace(/_/g, ' ')}</span>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>Date</span>
                  <span>{fmtDate(viewConcern.createdAt)}</span>
                  <span style={{ color: '#6B7280', fontWeight: 500 }}>Status</span>
                  <span><span className={viewConcern.status === 'resolved' ? 'bb-badge bb-badge--green' : 'bb-badge bb-badge--gray'}>{viewConcern.status === 'resolved' ? 'Resolved' : 'Open'}</span></span>
                </div>
                <div className="bb-form-group" style={{ marginBottom: 12 }}>
                  <label style={{ fontWeight: 500, fontSize: 13, color: '#6B7280' }}>Description</label>
                  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, fontSize: 14, whiteSpace: 'pre-wrap', minHeight: 60 }}>{safeStr(viewConcern.description) || '—'}</div>
                </div>
                {safeStr(viewConcern.adminNotes) && (
                  <div className="bb-form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontWeight: 500, fontSize: 13, color: '#6B7280' }}>Admin Notes</label>
                    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: 12, fontSize: 14, whiteSpace: 'pre-wrap' }}>{safeStr(viewConcern.adminNotes)}</div>
                  </div>
                )}
                <div className="bb-editor-actions">
                  {viewConcern.status !== 'resolved' && (
                    <button className="bb-btn-primary" onClick={() => { setResolveModal(viewConcern.id); setResolveNotes(''); setViewConcern(null); }}>Resolve</button>
                  )}
                  <button className="bb-btn-outline-sm" onClick={() => setViewConcern(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Resolve Modal */}
          {resolveModal !== null && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Resolve Concern</h3>
                <div className="bb-form-group">
                  <label>Admin Notes</label>
                  <textarea className="bb-textarea" rows={4} value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} placeholder="Describe how this concern was resolved or any follow-up actions taken." />
                </div>
                <div className="bb-editor-actions">
                  <button className="bb-btn-primary" onClick={handleResolveConcern}>Mark Resolved</button>
                  <button className="bb-btn-outline-sm" onClick={() => { setResolveModal(null); setResolveNotes(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {concernsLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading concerns…</td></tr></tbody></table>
          ) : concerns.length === 0 ? (
            <div className="bb-empty-state">No concerns logged.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Reported By</th><th>Subject</th><th>Type</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {concerns.map(c => (
                  <tr key={c.id}>
                    <td>
                      {safeStr(c.reporterName) || safeStr(c.reporterId) || '—'}
                      {safeStr(c.reporterRole) && <span style={{ color: '#6B7280', fontSize: 11, marginLeft: 4 }}>({safeStr(c.reporterRole)})</span>}
                    </td>
                    <td>{safeStr(c.subjectName) || safeStr(c.subjectId) || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(safeStr(c.concernType) || '—').replace(/_/g, ' ')}</td>
                    <td>{fmtDate(c.createdAt)}</td>
                    <td>
                      <span className={c.status === 'resolved' ? 'bb-badge bb-badge--green' : 'bb-badge bb-badge--gray'}>
                        {c.status === 'resolved' ? 'Resolved' : 'Open'}
                      </span>
                    </td>
                    <td style={{ display: 'flex', gap: 4 }}>
                      <button className="bb-btn-outline-sm" onClick={() => setViewConcern(c)}>View</button>
                      {c.status !== 'resolved' && (
                        <button className="bb-btn-sm" onClick={() => { setResolveModal(c.id); setResolveNotes(''); }}>Resolve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
